import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase';

function Conversations() {
  const [businessId, setBusinessId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const parseSummary = (summary) => {
    if (!summary) return 'N/A';
    try {
      const cleaned = summary.replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(cleaned);
      const parts = [];
      if (parsed.customer_name) parts.push(parsed.customer_name);
      if (parsed.need) parts.push(parsed.need);
      if (parsed.best_callback_time) parts.push(`Callback: ${parsed.best_callback_time}`);
      return parts.length > 0 ? parts.join(' · ') : 'N/A';
    } catch {
      return summary.replace(/```json|```/g, '').trim() || 'N/A';
    }
  };

  useEffect(() => {
    const fetchBusinessId = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: business } = await supabase.from('businesses').select('id').eq('owner_email', user.email).single();
      if (business) setBusinessId(business.id);
    };
    fetchBusinessId();
  }, []);

  useEffect(() => {
    if (!businessId) return;

    const fetchConversations = async () => {
      setLoading(true);
      setError('');

      const { data, error: fetchError } = await supabase
        .from('conversations')
        .select('id, customer_phone, summary, status, created_at')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (fetchError) {
        setError('Unable to load conversations. Please try again.');
        setLoading(false);
        return;
      }

      setConversations((data || []).map((c) => ({
        id: c.id,
        phone: c.customer_phone || 'Unknown',
        need: parseSummary(c.summary),
        status: c.status || 'New',
        createdAt: c.created_at,
      })));
      setLoading(false);
    };

    fetchConversations();
  }, [businessId]);

  const formatDateTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const badgeStyle = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'called back') return { backgroundColor: '#ecfdf3', color: '#166534', borderColor: '#bbf7d0' };
    if (s === 'closed') return { backgroundColor: '#f3f4f6', color: '#374151', borderColor: '#e5e7eb' };
    return { backgroundColor: '#EEF3FF', color: '#1A56DB', borderColor: '#C3D2F7' };
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F4F7FF', padding: '32px', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <div style={{ maxWidth: '1120px', margin: '0 auto' }}>
        <header style={{ marginBottom: '32px' }}>
          <h1 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '32px', fontWeight: 800, color: '#0C1A2E', letterSpacing: '-0.5px' }}>Conversations</h1>
          <p style={{ marginTop: '6px', color: '#5A6B84', fontSize: '14px' }}>Full history of AI conversations with your customers.</p>
        </header>

        {error && (
          <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '10px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '14px' }}>{error}</div>
        )}

        <section style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #DDE3F0', boxShadow: '0 2px 8px rgba(26,86,219,0.06)', overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #DDE3F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '18px', fontWeight: 700, color: '#0C1A2E' }}>All Conversations</h2>
            {loading && <span style={{ fontSize: '12px', color: '#9AABBE' }}>Loading…</span>}
          </div>

          {conversations.length === 0 && !loading ? (
            <div style={{ padding: '24px', fontSize: '14px', color: '#5A6B84' }}>No conversations found yet.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F4F7FF' }}>
                    {['Customer Phone', 'Date & Time', 'What They Needed', 'Status', ''].map((h, i) => (
                      <th key={i} style={{ textAlign: i === 4 ? 'right' : 'left', padding: '10px 16px', fontWeight: 600, color: '#5A6B84', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #DDE3F0' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {conversations.map((c) => {
                    const badge = badgeStyle(c.status);
                    return (
                      <tr key={c.id} style={{ borderBottom: '1px solid #EEF2FF' }}>
                        <td style={{ padding: '12px 16px', color: '#0C1A2E', fontWeight: 600 }}>{c.phone}</td>
                        <td style={{ padding: '12px 16px', color: '#3D4F66', whiteSpace: 'nowrap' }}>{formatDateTime(c.createdAt)}</td>
                        <td style={{ padding: '12px 16px', color: '#3D4F66', maxWidth: '380px' }}>
                          <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{c.need}</span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 600, border: `1px solid ${badge.borderColor}`, backgroundColor: badge.backgroundColor, color: badge.color, textTransform: 'capitalize' }}>{c.status}</span>
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                          {c.phone && c.phone !== 'Unknown' ? (
                            <a href={`tel:${c.phone}`} style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 14px', borderRadius: '999px', backgroundColor: '#22C55E', color: '#fff', fontSize: '12px', fontWeight: 700, textDecoration: 'none' }}>Call</a>
                          ) : (
                            <span style={{ fontSize: '12px', color: '#9AABBE' }}>No number</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default Conversations;